import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Radar, Eye, EyeOff, BarChart3, Users, Mail, Instagram, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { PasswordStrength, isPasswordValid } from '@/components/PasswordStrength';
import { Checkbox } from '@/components/ui/checkbox';

const RESEND_COOLDOWN_SECONDS = 60;


export default function Auth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('signin');
  const [consentChecked, setConsentChecked] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [signupAttempted, setSignupAttempted] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const { signIn, signUp, resendConfirmation, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // 60-second frontend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const startResendCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
  }, []);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    const { error } = await signIn(email, password);
    
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Signed in successfully!",
      });
    }
    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await signUp(email, password, fullName);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setSignupAttempted(true);
      startResendCooldown();
      setConsentChecked(false);
      setConfirmPassword('');
      setShowPassword(false);
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) {
      toast({
        title: 'Google sign-in failed',
        description: error.message,
        variant: 'destructive',
      });
      setLoading(false);
    }
    // On success the browser redirects to Google, so no further handling is needed.
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);

    const { error } = await resendConfirmation(email);

    if (error) {
      const msg = error.message?.toLowerCase?.() ?? '';
      const isRateLimit = msg.includes('rate limit') || msg.includes('security') || msg.includes('wait');
      toast({
        title: isRateLimit ? "Please wait" : "Error",
        description: isRateLimit
          ? "Please wait a moment before requesting another email."
          : error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Check your inbox",
        description: "If your email needs confirming, we've sent a new link.",
      });
      startResendCooldown();
    }
    setResendLoading(false);
  };

  const features = [
    { icon: Radar, label: "Radar Analysis", desc: "Multi-attribute player profiling" },
    { icon: Eye, label: "Live Scouting", desc: "Real-time session tracking" },
    { icon: BarChart3, label: "Comparisons", desc: "Side-by-side player analytics" },
    { icon: Users, label: "Watchlists", desc: "Track prospects effortlessly" },
  ];

  return (
    <div className="min-h-screen bg-background flex relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
      </div>

      {/* Hero Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero" />
        <div className="absolute inset-0 bg-black/20" />
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(hsl(0 0% 100% / 0.6) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 0.6) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse 70% 60% at 50% 50%, black, transparent)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 50%, black, transparent)",
          }}
        />
        
        {/* Decorative radar circles */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[560px] opacity-20 motion-safe:animate-[spin_90s_linear_infinite]">
          <div className="absolute inset-0 rounded-full border border-white/30" />
          <div className="absolute inset-[15%] rounded-full border border-white/20" />
          <div className="absolute inset-[30%] rounded-full border border-white/15" />
          <div className="absolute inset-[45%] rounded-full border border-white/10" />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16 text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="text-xl font-display font-bold tracking-tight">HalftimeScout</span>
            </div>

            <h1 className="text-5xl xl:text-6xl font-display font-bold leading-[1.02] tracking-[-0.035em] mb-5">
              Scout faster.
              <br />
              <span className="text-white/70">Discover smarter.</span>
            </h1>
            
            <p className="text-lg text-white/60 max-w-md mb-10 leading-relaxed">
              The professional platform for football fans who demand precision, speed, and insight.
            </p>

            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, i) => (
                <motion.div
                  key={feature.label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                  className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.07] backdrop-blur-sm p-3.5"
                >
                  <feature.icon className="h-5 w-5 text-white/80 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white/90">{feature.label}</p>
                    <p className="text-xs text-white/50">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Panel — Auth Form */}
      <main className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <Card className="relative surface shadow-elevated p-1 sm:p-3">
            <CardHeader className="text-center pb-2">
              <div className="flex items-center justify-center gap-3 mb-2 lg:hidden">
                <CardTitle className="text-lg font-display font-bold tracking-tight">HalftimeScout</CardTitle>
              </div>
              <CardTitle className="hidden lg:block text-2xl font-display font-bold tracking-tight">Welcome back</CardTitle>
              <CardDescription className="text-muted-foreground">
                Sign in to your scouting dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="signin" value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 h-11 mb-5">
                  <TabsTrigger value="signin">Sign In</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>
                
                <TabsContent value="signin">
                  <div className="space-y-4 mb-4">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                    >
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                      Continue with Google
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                      By continuing, you agree to the{' '}
                      <Link to="/terms" className="underline hover:text-foreground transition-colors">Terms of Service</Link>
                      {' '}and confirm you are at least 16 years old.
                    </p>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="bg-card px-2 text-muted-foreground">
                          or continue with email
                        </span>
                      </div>
                    </div>
                  </div>
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="scout@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" size="lg" className="w-full" disabled={loading}>
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Sign In
                    </Button>
                    
                    <div className="text-center mt-4">
                      <Link 
                        to="/forgot-password" 
                        className="text-sm text-primary hover:underline"
                      >
                        Forgot your password?
                      </Link>
                    </div>
                  </form>
                </TabsContent>
                
                <TabsContent value="signup">
                  {signupAttempted ? (
                    <div className="space-y-4">
                      <div className="rounded-xl bg-info/10 border border-info/25 p-4 text-sm text-muted-foreground space-y-2">
                        <p>
                          Almost there! If this email isn’t already registered, we’ve sent a confirmation link. Please check your inbox (and spam). Didn’t get it? You can resend below.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={handleResend}
                        disabled={resendCooldown > 0 || resendLoading}
                      >
                        {resendLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {!resendLoading && <Mail className="mr-2 h-4 w-4" />}
                        {resendCooldown > 0
                          ? `Resend available in ${resendCooldown}s…`
                          : 'Resend confirmation email'}
                      </Button>
                    </div>
                  ) : (<>
                    <div className="space-y-4 mb-4">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                      >
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        Continue with Google
                      </Button>
                      <p className="text-xs text-center text-muted-foreground">
                        By continuing, you agree to the{' '}
                        <Link to="/terms" className="underline hover:text-foreground transition-colors">Terms of Service</Link>
                        {' '}and confirm you are at least 16 years old.
                      </p>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="bg-card px-2 text-muted-foreground">
                            or continue with email
                          </span>
                        </div>
                      </div>
                    </div>
                    <form onSubmit={handleSignUp} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="fullName">Full Name</Label>
                        <Input
                          id="fullName"
                          type="text"
                          placeholder="John Doe"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signupEmail">Email</Label>
                        <Input
                          id="signupEmail"
                          type="email"
                          placeholder="scout@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signupPassword">Password</Label>
                        <div className="relative">
                          <Input
                            id="signupPassword"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? (
                              <Eye className="h-4 w-4" />
                            ) : (
                              <EyeOff className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                        {activeTab === 'signup' && (
                          <PasswordStrength password={password} />
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">Confirm Password</Label>
                        <div className="relative">
                          <Input
                            id="confirmPassword"
                            type={showPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? (
                              <Eye className="h-4 w-4" />
                            ) : (
                              <EyeOff className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                        {confirmPassword.length > 0 && password !== confirmPassword && (
                          <p role="alert" className="flex items-center gap-1.5 text-xs font-medium text-danger"><AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />Passwords do not match</p>
                        )}
                      </div>
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id="consent"
                          checked={consentChecked}
                          onCheckedChange={(checked) => setConsentChecked(checked === true)}
                        />
                        <label htmlFor="consent" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                          I am at least 16 years old, and I agree to the{" "}
                          <Link to="/privacy" className="underline hover:text-foreground transition-colors">Privacy Policy</Link>
                          {" "}and{" "}
                          <Link to="/terms" className="underline hover:text-foreground transition-colors">Terms of Service</Link>
                        </label>
                      </div>
                      <Button
                        type="submit"
                        size="lg"
                        className="w-full"
                        disabled={loading || !isPasswordValid(password) || password !== confirmPassword || !consentChecked}
                      >
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Sign Up
                      </Button>
                    </form>
                  </>)}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
          <div className="mt-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-4">
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
            <a
              href="https://instagram.com/halftimescout"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="hover:text-foreground transition-colors"
            >
              <Instagram className="h-4 w-4" />
            </a>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
